<template>
	<div class="select-and-data-picker">
		<el-select
			class="select"
			:class="{ focus: currentFocus === 'select' }"
			v-bind="actualSelectConfig"
			:value="value.select"
			:size="size"
			@input="selectInput"
			@change="selectChange"
			@clear="selectClear"
			@focus="selectFocus"
		>
			<el-option
				v-for="(item, index) in options"
				:key="index"
				:label="item[labelKey || 'label']"
				:value="item[valueKey || 'value']"
			/>
		</el-select>
		<el-date-picker
			class="date-picker"
			:class="{ focus: currentFocus === 'date' }"
			v-bind="actualDataPickerConfig"
			:disabled="disabled"
			:value="value.date"
			:size="size"
			@focus="dateFocus"
			@input="dateInput"
			@change="dateChange"
		/>
	</div>
</template>

<script>
/**
 * @description 下拉选择器和时间选择器组合
 * @param {Object} value - 绑定值, { select: '', date: '' }, select为下拉选择器值，date为时间选择器值, 支持使用v-model双向绑定
 * @param {Array} options - 下拉选择器选项
 * @param {Object} selectConfig - 下拉选择器配置
 * @param {Object} dataPickerConfig - 时间选择器配置
 * @param {String} labelKey - 下拉选择label字段，默认label
 * @param {String} valueKey - 下拉选择value字段，默认value
 *
 * @event input - 绑定值改变时触发
 * @event select-input - 下拉选择器值改变时触发
 * @event select-change - 下拉选择器值改变时触发
 * @event select-clear - 下拉选择器清空时触发
 * @event select-focus - 下拉选择器获取焦点时触发
 * @event date-input - 时间选择器值改变时触发
 * @event date-change - 时间选择器值改变时触发
 * @event date-focus - 时间选择器获取焦点时触发
 */

export default {
	name: "SelectAndDataPicker",
	inheritAttrs: false,
	props: {
		value: {
			type: Object,
			default: () => ({})
		},
		options: {
			type: Array,
			default: () => []
		},
		/**
		 * selectConfig: 下拉选择器配置
		 */
		selectConfig: {
			type: Object,
			default: () => ({})
		},
		/**
		 * dataPickerConfig: 时间选择器配置
		 */
		dataPickerConfig: {
			type: Object,
			default: () => ({})
		},
		/**
		 * labelKey: 下拉选择label字段，默认label
		 */
		labelKey: {
			type: String,
			default: "label"
		},
		/**
		 * valueKey: 下拉选择value字段，默认value
		 */
		valueKey: {
			type: String,
			default: "value"
		},
		size: {
			type: String,
			default: "small"
		}
	},
	data() {
		return {
			actualSelectConfig: {
				placeholder: this.$t("pleaseSelect"),
				...this.selectConfig
			},
			actualDataPickerConfig: {
				startPlaceholder: this.$t("startTime"),
				endPlaceholder: this.$t("endTime"),
				valueFormat: "yyyy-MM-dd",
				rangeSeparator: this.$t("to"),
				type: "daterange",
				...this.dataPickerConfig
			},
			currentFocus: ""
		};
	},
	methods: {
		selectInput(value) {
			this.$emit("input", {
				select: value
			});

			this.$emit("select-input", value);
		},
		selectChange(value) {
			this.$emit("select-change", value);
		},
		selectClear() {
			this.$emit("input", {});
			this.$emit("select-clear");
			this.selectChange();

			if (this.value.date) {
				this.dateChange();
			}
		},
		selectFocus() {
			this.currentFocus = "select";
			this.$emit("select-focus");
		},
		dateInput(value) {
			this.$emit("input", {
				...this.value,
				date: value
			});
			this.$emit("date-input", value);
		},
		dateChange(value) {
			this.$emit("date-change", value);
		},
		dateFocus() {
			this.currentFocus = "date";
			this.$emit("date-focus");
		}
	},
	computed: {
		disabled() {
			return !this.value.select || this.value.select.length === 0;
		}
	}
};
</script>

<style lang="scss" scoped>
.select-and-data-picker {
	width: 100%;
	display: flex;
	align-items: center;

	.select {
		width: 1/3 * 100%;
		margin-right: -1px;

		&.focus {
			z-index: 2;
		}

		::v-deep .el-input__inner {
			border: 1px solid #dcdde1;
			border-radius: 4px 0px 0px 4px;
		}
	}

	.date-picker {
		width: 2/3 * 100%;
		border: 1px solid #dcdde1;
		border-radius: 0px 4px 4px 0px;

		&.focus {
			z-index: 2;
		}
	}
}
</style>
